import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  DATA_TRANSFER_RUN_HISTORY_ACTIVE_POLL_INTERVAL_MS,
  DATA_TRANSFER_RUN_HISTORY_POLL_INTERVAL_MS,
  pollingOptions,
} from 'src/constants/polling.constants';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';
import { formatDisplayDateTime } from 'src/helpers/date.helpers';
import { useGetDataTransferRunsQuery } from 'src/redux/operations/operations.endpoints';
import {
  formatRowCount,
  formatRunDuration,
} from '../helpers/data-transfers.helpers';
import type { ClientDataTransfer, DataTransferRun } from '../types/data-transfers.types';
import { DataTransferStatusBadge } from './data-transfer-status-badge';
import { ETL_RUNS_PAGE_SIZE, EtlRunsPagination } from './etl-runs-pagination';

function RunErrorPreview({ message }: { message: string }) {
  const [expanded, setExpanded] = useState(false);
  const isLong = message.length > 120;
  const displayText = expanded || !isLong ? message : `${message.slice(0, 120)}…`;

  return (
    <div className="max-w-md text-sm text-red-700">
      <p className="whitespace-pre-wrap break-words">
        {displayText}
      </p>
      {isLong ? (
        <button
          type="button"
          className="mt-1 text-xs font-medium underline-offset-2 hover:underline"
          onClick={() => setExpanded((value) => !value)}
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      ) : null}
    </div>
  );
}

function formatTriggeredBy(run: DataTransferRun): string {
  if (run.trigger_type !== 'manual') {
    return '—';
  }
  return run.triggered_by?.name ?? run.triggered_by?.email ?? '—';
}

interface DataTransferRunHistoryProps {
  transfer: ClientDataTransfer | null;
  onSelectRun: (run: DataTransferRun) => void;
}

export function DataTransferRunHistory({ transfer, onSelectRun }: DataTransferRunHistoryProps) {
  const [page, setPage] = useState(1);
  const { data, isLoading, isError, isFetching } = useGetDataTransferRunsQuery(
    {
      transferId: transfer?.id,
      page,
      page_size: ETL_RUNS_PAGE_SIZE,
    },
    {
      skip: !transfer?.id,
      ...pollingOptions(DATA_TRANSFER_RUN_HISTORY_POLL_INTERVAL_MS),
      pollingInterval: transfer?.last_status === 'running'
        ? DATA_TRANSFER_RUN_HISTORY_ACTIVE_POLL_INTERVAL_MS
        : DATA_TRANSFER_RUN_HISTORY_POLL_INTERVAL_MS,
    },
  );

  if (!transfer) {
    return null;
  }

  if (isLoading) {
    return (
      <div
        className="flex items-center gap-2 text-sm text-gray-500"
        role="status"
      >
        <Loader2 className="size-4 animate-spin" />
        Loading run history…
      </div>
    );
  }

  if (isError) {
    return (
      <div
        className="rounded-md border border-red-101 bg-red-101 p-4 text-sm text-red-700"
        role="alert"
      >
        Could not load run history for
        {' '}
        {transfer.name}
        .
      </div>
    );
  }

  const runs = data?.items ?? [];
  const total = data?.total_objects ?? 0;
  const pageSize = data?.page_size ?? ETL_RUNS_PAGE_SIZE;
  const totalPages = data?.total_pages ?? Math.max(1, Math.ceil(total / pageSize));

  if (!runs.length) {
    return (
      <p className="text-sm text-gray-500">
        No runs recorded yet for
        {' '}
        {transfer.name}
        .
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="overflow-hidden rounded-md border border-gray-400">
        <Table>
          <TableHeader>
            <TableRow className="border-gray-400 bg-gray-200 hover:bg-gray-200">
              <TableHead className="px-4 py-3 text-sm font-medium text-gray-700">
                Status
              </TableHead>
              <TableHead className="px-4 py-3 text-sm font-medium text-gray-700">
                Trigger
              </TableHead>
              <TableHead className="px-4 py-3 text-sm font-medium text-gray-700">
                Triggered by
              </TableHead>
              <TableHead className="px-4 py-3 text-sm font-medium text-gray-700">
                Started
              </TableHead>
              <TableHead className="px-4 py-3 text-sm font-medium text-gray-700">
                Duration
              </TableHead>
              <TableHead className="px-4 py-3 text-sm font-medium text-gray-700">
                Rows
              </TableHead>
              <TableHead className="px-4 py-3 text-sm font-medium text-gray-700">
                Details
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {runs.map((run) => (
              <TableRow
                key={run.id}
                className="cursor-pointer border-b border-gray-300 align-top hover:bg-gray-200/80"
                onClick={() => onSelectRun(run)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onSelectRun(run);
                  }
                }}
                tabIndex={0}
                role="button"
                aria-label={`Open run ${run.id}`}
              >
                <TableCell className="px-4 py-3">
                  <DataTransferStatusBadge status={run.status} />
                </TableCell>
                <TableCell className="px-4 py-3 text-sm capitalize text-gray-700">
                  {run.trigger_type}
                </TableCell>
                <TableCell className="px-4 py-3 text-sm text-gray-700">
                  {formatTriggeredBy(run)}
                </TableCell>
                <TableCell className="px-4 py-3 text-sm text-black">
                  {run.started_at ? formatDisplayDateTime(run.started_at) : '—'}
                </TableCell>
                <TableCell className="px-4 py-3 text-sm text-gray-700">
                  {formatRunDuration(run.started_at, run.finished_at)}
                </TableCell>
                <TableCell className="px-4 py-3 text-sm text-gray-700">
                  {formatRowCount(run.row_count)}
                </TableCell>
                <TableCell className="px-4 py-3 text-sm text-gray-700">
                  {run.status === 'failed' && run.error_message ? (
                    <RunErrorPreview message={run.error_message} />
                  ) : (
                    '—'
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <EtlRunsPagination
        page={page}
        totalPages={totalPages}
        totalObjects={total}
        pageSize={pageSize}
        disabled={isFetching}
        onPageChange={setPage}
      />
    </div>
  );
}
